// ============================================================
// Cloupak 国际化词典（zh 中文 / en English）
//
// zh 为基准词典：键集合即翻译键全集。
// en 使用 Record<TranslationKey, ...> 约束，缺键会在编译期报错。
// 值支持两种形式：
//   - string：普通文案，支持 {name} 插值
//   - { one, other }：复数形式，params.count === 1 时取 one
// ============================================================

/** 复数形式（en 需要，zh 两者相同） */
export interface PluralForms {
  one: string;
  other: string;
}

export type TranslationValue = string | PluralForms;

export const zh = {
  // ---------- 语言切换 ----------
  'lang.switch': '切换语言',
  'lang.zh': '中文',
  'lang.en': 'English',

  // ---------- 通用 ----------
  'common.loading': '加载中…',
  'common.retry': '重试',
  'common.cancel': '取消',
  'common.reset': '重置',
  'common.remove': '移除',
  'common.refreshing': '刷新中…',
  'common.backToApps': '返回应用列表',
  'common.loadFailed': '加载失败：{message}',
  'common.appCount': { one: '{count} 个应用', other: '{count} 个应用' },

  // ---------- 套餐 ----------
  'plan.free': '免费版',
  'plan.pro': '专业版',
  'plan.enterprise': '企业版',

  // ---------- 外壳（顶栏 / 页脚 / 404） ----------
  'app.brandAria': 'Cloupak 首页',
  'app.navAria': '主导航',
  'app.footerTagline': '一键上云',
  'nav.apps': '我的应用',
  'nav.create': '创建应用',
  'nav.settings': '设置',
  'nav.logout': '退出',
  'nav.login': '登录',
  'notFound.title': '页面走丢了',
  'notFound.desc': '你访问的页面不存在或已被移动。',
  'notFound.back': '返回应用列表',

  // ---------- 构建状态徽章 ----------
  'status.idle': '待构建',
  'status.uploading': '上传中',
  'status.building': '构建中',
  'status.success': '已就绪',
  'status.failed': '失败',

  // ---------- 相对时间 ----------
  'relTime.justNow': '刚刚',
  'relTime.minutes': '{n}分钟前',
  'relTime.hours': '{n}小时前',
  'relTime.days': '{n}天前',

  // ---------- API 报错兜底（非 React 代码调用） ----------
  'apiError.requestFailed': '请求失败（HTTP {status}）',
  'apiError.downloadFailed': '下载失败（HTTP {status}）',
  'apiError.code.LOGIN_FAILED': '登录失败',
  'apiError.code.EMAIL_ALREADY_EXISTS': '该邮箱已注册',
  'apiError.code.NO_PASSWORD_SET': '该账号未设置密码，请使用第三方登录',
  'apiError.code.WEAK_PASSWORD': '密码强度不足：需 8-64 位且同时包含字母和数字',
  'apiError.code.INVALID_TOKEN': '认证失败，请重新登录',
  'apiError.code.TOKEN_EXPIRED': '登录已过期，请重新登录',
  'apiError.code.MISSING_AUTH': '请先登录',
  'apiError.code.CREATE_USER_FAILED': '创建用户失败',
  'apiError.code.INVALID_EMAIL': '邮箱格式不正确',
  'apiError.code.MISSING_FIELDS': '请填写完整信息',
  'apiError.code.INVALID_CREDENTIALS': '邮箱或密码错误',
  'apiError.code.USER_BANNED': '账号已被封禁',
  'apiError.code.REGISTER_FAILED': '注册失败',
  'apiError.code.UPLOAD_FAILED': '上传失败',
  'apiError.code.APK_NOT_FOUND': 'APK 尚未生成，请稍后重试或重新构建',
  'apiError.code.APK_DOWNLOAD_FAILED': 'APK 下载失败',
  'apiError.code.QUERY_FAILED': '查询失败',
  'apiError.code.CREATE_APP_FAILED': '创建应用失败',
  'apiError.code.INVALID_FILE_TYPE': '仅支持 ZIP 格式文件',
  'apiError.code.DELETE_FAILED': '删除失败',
  'apiError.code.NO_FILE': '未提供文件或文件格式错误',
  'apiError.code.FILE_TOO_LARGE': '文件大小超过 50MB 限制',
  'apiError.code.APP_NOT_FOUND': '应用不存在或无权访问',
  'apiError.code.INVALID_PLAN': '无效的套餐',
  'apiError.code.ALREADY_SUBSCRIBED': '你已是该套餐',
  'apiError.code.INVALID_BODY': '无效的请求',
  'apiError.code.ALIPAY_NOT_CONFIGURED': '支付宝支付未配置',
  'apiError.code.XUNHUPAY_NOT_CONFIGURED': '该支付通道未配置',
  'apiError.code.WAFFO_NOT_CONFIGURED': '该支付方式未配置',
  'apiError.code.ALIPAY_ERROR': '创建订单失败',
  'apiError.code.XUNHUPAY_ERROR': '创建订单失败',
  'apiError.code.WAFFO_ERROR': '创建支付会话失败',
  'apiError.code.CANCEL_FAILED': '取消失败',
  'apiError.code.NO_ACTIVE_SUB': '没有活跃订阅',
  'apiError.code.NOT_FOUND': '记录不存在',
  'apiError.code.QUOTA_EXCEEDED': '构建配额不足',
  'apiError.code.TRIGGER_FAILED': '触发构建失败',
  'apiError.code.BUILD_ERROR': '触发构建异常',
  'apiError.code.NO_SOURCE': '未找到上传的源码文件，请先上传 ZIP',
  'apiError.code.BUILD_IN_PROGRESS': '应用正在构建中，请等待构建完成后再更新',
  'apiError.code.INVALID_NAME': '应用名称不能为空',
  'apiError.code.NAME_TOO_LONG': '应用名称不能超过 40 个字符',
  'apiError.code.UPDATE_APP_FAILED': '更新应用失败',
  'apiError.code.INTERNAL_ERROR': '服务器内部错误',

  // ---------- Landing ----------
  'landing.hero.titleA': '写完代码，',
  'landing.hero.titleB': '一键打包',
  'landing.hero.titleC': '成手机App。',
  'landing.hero.sub':
    '把你的 Web 应用变成可安装的 APK——自动部署到云端、自动配置数据库、自动打包。不写一行部署代码。',
  'landing.hero.ctaConsole': '进入控制台',
  'landing.hero.ctaStart': '免费开始',
  'landing.hero.ctaHow': '了解原理',
  'landing.hero.trust':
    '支持邮箱登录 · 无需注册公司 · 无需 ICP 备案 · 无需 Android 开发环境',

  'landing.flow1.label': '上传文件夹或粘贴代码',
  'landing.flow1.detail': '拖拽 ZIP 文件 / 粘贴 HTML',
  'landing.flow2.label': '自动部署 + 打包',
  'landing.flow2.detail': 'R2 + Cloudflare + Gradle',
  'landing.flow3.label': '扫码安装到手机',
  'landing.flow3.detail': 'APK 下载 + 二维码',

  'landing.how.title': '三步上云',
  'landing.how.sub': '从代码到手机 App，全程自动',
  'landing.step1.title': '上传你的应用',
  'landing.step1.desc':
    '拖拽文件夹上传，或粘贴代码。支持 React、Vue、HTML 等所有 Web 框架，系统自动识别项目类型。',
  'landing.step2.title': '自动部署打包',
  'landing.step2.desc':
    '系统自动部署到 Cloudflare、配置数据同步、用 Gradle 打包成 APK。约 5 分钟完成。',
  'landing.step3.title': '扫码安装',
  'landing.step3.desc':
    '用手机扫描二维码下载 APK，一键安装。改了代码？点"更新"自动重新部署。',

  'landing.features.title': '为什么选 Cloupak',
  'landing.features.sub': '不只是一个打包工具，而是完整的云端托管方案',
  'landing.feat1.title': '零配置一键打包',
  'landing.feat1.desc': '无需安装 Android Studio、无需配 Gradle、无需懂签名。上传代码即出 APK。',
  'landing.feat2.title': '云端数据托管',
  'landing.feat2.desc': '自动配置数据同步 SDK。你的 App 数据安全托管在云端，多设备自动同步。',
  'landing.feat3.title': '改代码自动更新',
  'landing.feat3.desc':
    'WebView 技术让已安装的 App 自动显示最新内容。改完代码点"更新"，无需重新下载 APK。',
  'landing.feat4.title': '原生 Android 支持',
  'landing.feat4.desc': '不仅支持 Web 项目打包，还支持原生 Android Kotlin/Java 项目，自动注入数据同步 SDK。',
  'landing.feat5.title': '5 分钟完成',
  'landing.feat5.desc': '从提交代码到 APK 可下载，全流程 P95 控制在 10 分钟以内。不用等审核。',
  'landing.feat6.title': '个人专属 App',
  'landing.feat6.desc': '给家人做个待办清单、给自己做个记账本——自己的 App 自己装，不上应用商店也行。',

  'landing.pricing.title': '简单定价',
  'landing.pricing.sub': '免费开始，满意再付费',
  'landing.pricing.tierFree': '免费版',
  'landing.pricing.tierPersonal': '个人版',
  'landing.pricing.freeAmount': '¥0',
  'landing.pricing.personalAmount': '¥19',
  'landing.pricing.personalAlt': ' / $3/mo',
  'landing.pricing.perMonth': '/月',
  'landing.price.f1': '1 个应用',
  'landing.price.f1Personal': '3 个应用',
  'landing.price.f2': '自动部署 + APK 打包',
  'landing.price.f3': '云端数据同步',
  'landing.price.f4': '二维码下载',
  'landing.price.f5': '无水印',
  'landing.price.f6': '多设备同步',
  'landing.price.f7': '1GB 数据存储',
  'landing.pricing.upgradePersonal': '升级个人版',
  'landing.pricing.note':
    '国内支持支付宝/微信扫码（¥19/月）· 国际支持信用卡支付（$3/月）· 权益相同',

  'landing.cta.title': '现在就试试？',
  'landing.cta.sub': '30 秒内创建你的第一个 App · 支持邮箱登录',
  'landing.footer': 'Cloupak — 让每个人都能拥有自己的手机 App · 2026',

  // ---------- 登录 / 注册 ----------
  'auth.tabLogin': '登录',
  'auth.tabRegister': '注册',
  'auth.loginTitle': '欢迎回来',
  'auth.registerTitle': '创建账户',
  'auth.loginSub': '登录以管理你的 Cloupak 应用',
  'auth.registerSub': '注册即可开始构建和部署应用',
  'auth.name': '用户名',
  'auth.email': '邮箱',
  'auth.password': '密码',
  'auth.namePlaceholder': '你的名字',
  'auth.passwordPlaceholder': '8 位以上，含字母和数字',
  'auth.submitting': '处理中…',
  'auth.submitLogin': '登录',
  'auth.submitRegister': '注册',
  'auth.errName': '请输入用户名',
  'auth.errEmail': '请输入有效的邮箱地址',
  'auth.errPasswordRequired': '请输入密码',
  'auth.pwdErrLength': '密码长度需 8-64 位',
  'auth.pwdErrChars': '密码需同时包含字母和数字',
  'auth.pwdHint': '8-64 位，需同时包含字母和数字',
  'auth.errFailed': '操作失败，请稍后重试。',
  'auth.captchaLoadFailed': '人机验证组件加载失败，请检查网络后刷新重试（部分代理或广告拦截器会拦截 Cloudflare 验证服务）。',
  'auth.captchaPreparing': '正在准备人机验证…',
  'auth.captchaSlow': '人机验证响应较慢，正在自动重试…',
  'auth.forgotLink': '忘记密码？',
  'auth.forgotTitle': '找回密码',
  'auth.forgotSub': '输入注册邮箱，我们将发送密码重置链接',
  'auth.forgotSubmit': '发送重置邮件',
  'auth.forgotSent': '如果该邮箱已注册，重置邮件已发送。请在 30 分钟内完成重置（请记得检查垃圾邮件文件夹）。',
  'auth.backToLogin': '返回登录',
  'auth.resetTitle': '重置密码',
  'auth.resetSub': '请为您的主账号设置新密码',
  'auth.resetSubmit': '重置并登录',
  'auth.resetInvalidLink': '重置链接无效或已过期，请重新获取。',
  'auth.resetReRequest': '重新发送重置邮件',

  // ---------- Dashboard ----------
  "workspace.eyebrow": "你的工作空间",
  "workspace.dashboardSub": "从这里发布、更新和分享你的应用。",
  "workspace.publishApp": "发布应用",
  "workspace.overview": "工作空间概览",
  "workspace.introTitle": "你的项目，随时可用。",
  "workspace.introSub": "上传项目，交给我们构建，然后在手机或浏览器中打开。",
  "workspace.live": "已上线",
  "workspace.inProgress": "构建中",
  "workspace.appLibrary": "应用列表",
  "workspace.libraryHint": "你发布的所有应用",
  "workspace.emptyTitle": "让你的第一个项目上线",
  "workspace.emptyHint": "支持静态网页、前端项目和 Android 项目",
  "workspace.agentHint": "用 AI 助手开发项目？让它直接帮你发布。",
  "workspace.connectAgent": "连接 AI 助手",
  "workspace.cardReady": "已准备好，可在手机上安装或在线打开。",
  "workspace.cardFailed": "构建未完成，查看详情并重试。",
  "workspace.cardDraft": "项目已创建，上传源码即可开始。",
  "workspace.cardBuilding": "正在云端处理项目，完成后即可下载。",
  "workspace.updated": "更新于 {time}",
  "workspace.openApp": "打开应用",
  "workspace.manageApp": "下载与管理",
  "workspace.resolveBuild": "查看问题",
  "workspace.finishSetup": "继续设置",
  "workspace.viewBuild": "查看进度",
  "workspace.fileEmpty": "这个 ZIP 文件是空的，请选择包含项目文件的压缩包。",
  "workspace.fileLarge": "文件超过 50 MB，请移除依赖和生成文件后重新压缩。",
  "workspace.changeFile": "更换文件",
  "workspace.uploadOr": "或从电脑中选择文件",
  "workspace.chooseFile": "选择 ZIP 文件",
  "workspace.publishJourney": "发布步骤",
  "workspace.stepPrepare": "准备项目",
  "workspace.stepBuild": "云端构建",
  "workspace.stepInstall": "安装与分享",
  "workspace.publishEyebrow": "开始发布",
  "workspace.publishTitle": "发布你的应用",
  "workspace.publishSub": "给应用取一个名字，再上传项目。剩下的交给 Cloupak。",
  "workspace.projectDetails": "项目信息",
  "workspace.requiredFields": "两个字段均为必填",
  "workspace.nameExample": "例如：我的记账本",
  "workspace.publishError": "发布暂未完成",
  "workspace.retryHint": "你的项目记录已保留。检查文件后可以再次发布。",
  "workspace.retryPublish": "重试发布",
  "workspace.buildInCloud": "无需安装构建工具",
  "workspace.zipHelpTitle": "准备项目压缩包",
  "workspace.zipHelpSub": "将项目文件放进一个 ZIP 包，上传前检查以下内容。",
  "workspace.zipHelp1": "静态网页包含 index.html；前端项目包含 package.json；Android 项目包含 Gradle 配置。",
  "workspace.zipHelp2": "移除 node_modules、.git 和不需要的生成文件，保持文件小于 50 MB。",
  "workspace.zipHelp3": "请勿包含密码、API 密钥或 .env 文件。前端项目应能输出 dist 或 build 目录。",
  "workspace.supported": "支持的项目",
  "workspace.whatNext": "然后会发生什么？",
  "workspace.whatNextSub": "Cloupak 在云端构建项目。完成后，你可以下载 Android 安装包；网页项目还会获得在线访问链接。",
  "workspace.appOverview": "应用概览",
  "workspace.buildFailedTitle": "这次构建需要处理",
  "workspace.buildFailedSub": "在下方查看构建详情。你可以上传修复后的项目，或重试当前源码。",
  "workspace.draftTitle": "项目已创建",
  "workspace.draftSub": "在“更新应用”中上传项目文件，再开始构建。",
  "workspace.buildDetails": "构建详情",
  "workspace.technicalLabel": "日志与历史",
  "workspace.buildHistory": "最近构建",
  "workspace.noBuilds": "还没有构建记录。",
  "workspace.appId": "应用 ID",
  "workspace.shareTitle": "分享你的应用",
  "workspace.shareSub": "网页项目可以直接分享访问链接。Android 用户也可以扫描二维码获取安装包。",
  "workspace.deleteConfirm": "确定删除「{name}」？应用、文件和云端数据会被永久删除，无法恢复。",
  "workspace.cloudBuild": "正在云端构建",
  "workspace.buildingTitle": "正在准备你的应用",
  "workspace.buildingSub": "项目正在云端构建和打包。完成后，这里会自动显示结果。",
  "workspace.buildingHint": "你可以离开此页面，稍后从应用列表回来查看。",
  "workspace.readyEyebrow": "构建成功",
  "workspace.readyTitle": "你的应用已准备好",
  "workspace.readySub": "下载到 Android 手机，或在浏览器中打开。",
  "workspace.downloadAndroid": "下载 Android 应用",
  "workspace.scanPhone": "在手机上扫码",
  "workspace.androidOnly": "Android 安装包",
  "workspace.qrUnavailable": "二维码暂不可用，请使用下载按钮。",
  "workspace.copyLink": "复制应用链接",
  "workspace.copied": "链接已复制",
  "workspace.copyFallback": "请选中上方链接，手动复制。",
  "workspace.installHelp": "需要安装指引？",
  "workspace.newVersion": "上传新的项目文件",
  "workspace.publishUpdate": "发布新版本",
  "workspace.billingNav": "套餐与账单",
  "workspace.accountNav": "账户",
  "workspace.securityNav": "安全",
  "workspace.integrationsNav": "AI 助手",
  "workspace.settingsSub": "管理账户、套餐，以及与你的 AI 助手的连接。",
  "workspace.integrationSub": "创建访问令牌，让 AI 助手帮你发布和更新应用。",
  "workspace.landingEyebrow": "让创意走进日常",
  "workspace.landingTitle": "从你的项目，到你的手机。",
  "workspace.landingSub": "把你开发的网页或 Android 项目变成可安装的应用。上传 ZIP，Cloupak 为你完成云端构建、打包和发布。",
  "workspace.landingProof": "支持网页与 Android 项目 · 无需本地构建工具",
  "workspace.landingPreview": "产品预览 · 示例项目",
  "workspace.landingExample": "日常记账",
  "workspace.landingExampleSub": "你的创意，随时随地打开。",
  "workspace.landingPreviewReady": "已准备好安装",
  "workspace.landingWorkflow": "从项目到应用，只需三步",
  "workspace.landingWorkflowSub": "专注于创造，我们负责发布。",
  "workspace.landingFeatureTitle": "让你的项目更实用",
  "workspace.landingFeatureSub": "构建、托管和数据同步，在一个地方完成。",
  "workspace.landingCtaTitle": "你的下一个应用，从这里开始。",
  "workspace.landingCtaSub": "上传你的项目，看看它在手机上运行的样子。",
  'workspace.skipContent': '跳转到主要内容',
  "workspace.landingPrepare": "给应用取名，把项目压缩为 ZIP 并上传。支持静态网页、前端和 Android 项目。",
  "workspace.landingBuild": "Cloupak 识别并在云端构建项目。你可以在工作空间中查看构建结果。",
  "workspace.landingInstall": "下载 Android 安装包。网页项目还会获得可分享的在线链接。",
  "workspace.featurePackage": "云端打包",
  "workspace.featurePackageSub": "上传支持的项目，即可获得 Android 安装包。无需在电脑上配置构建或签名工具。",
  "workspace.featureSync": "数据随你同行",
  "workspace.featureSyncSub": "对于在浏览器中保存数据的网页应用，Cloupak 自动添加云端同步，让你在多台设备上使用。",
  "workspace.featureUpdate": "轻松发布新版本",
  "workspace.featureUpdateSub": "修改项目后，上传新的 ZIP 包。网页内容会更新到原有链接；原生 Android 更新需安装新版 APK。",
  'workspace.accountDetails': '账户标识',
  'workspace.viewPlans': '查看套餐',
  'dashboard.title': '我的应用',
  'dashboard.sub': '管理你的 Cloupak 应用与构建',
  'dashboard.create': '创建新应用',
  'dashboard.emptyTitle': '还没有应用',
  'dashboard.emptyDesc': '创建你的第一个应用，上传 ZIP 包即可开始构建。',
  'dashboard.createFirst': '创建第一个应用',

  // ---------- 创建应用 ----------
  'create.stage.creating': '正在创建应用…',
  'create.stage.uploading': '正在上传 ZIP 包…',
  'create.stage.building': '正在触发构建…',
  'create.stage.done': '完成，正在跳转…',
  'create.stage.error': '操作失败',
  'create.title': '创建新应用',
  'create.sub': '上传你的项目 ZIP 包，Cloupak 将自动完成构建与打包。',
  'create.nameLabel': '应用名称',
  'create.namePlaceholder': '例如：my-awesome-app',
  'create.nameHint': '这个名字会显示在应用图标下方（最多 40 字）。',
  'create.zipLabel': '上传项目 ZIP 包',
  'create.errZipOnly': '仅支持 .zip 格式的文件',
  'create.dropTitle': '拖拽 ZIP 文件到此处',
  'create.dropSub': '或点击选择文件',
  'create.dropHint': '支持 .zip 格式 · 最大 50MB',
  'create.submitFailed': '提交失败，请稍后重试。',
  'create.uploadAndBuild': '上传并构建',

  // ---------- 应用详情 ----------
  'detail.deleteFailed': '删除失败',
  'detail.notFound': '未找到该应用。',
  'detail.cardProgress': '部署进度',
  'detail.cardDone': '部署完成',
  'detail.cardFailed': '部署失败',
  'detail.cardIdle': '应用状态',
  'detail.building': '正在构建…',
  'detail.buildingHint': '构建中，页面每 3 秒自动刷新状态…',
  'detail.liveUrl': '在线访问',
  'detail.downloadApk': '下载 APK',
  'detail.downloading': '下载中…',
  'detail.retryDownload': '重试下载',
  'detail.clickToDownload': '点击下载 APK',
  'detail.downloadFailed': '下载失败',
  'detail.scanTitle': '扫码下载 APK',
  'detail.scanAlt': 'APK下载二维码',
  'detail.scanHint': '用 Android 手机扫描二维码直接下载安装',
  'detail.guide.android': '手机安装（Android）',
  'detail.guide.android.1': '点击上方"下载 APK"链接，或扫描二维码下载安装文件',
  'detail.guide.android.2': '系统提示"允许安装未知应用"时，点击允许',
  'detail.guide.android.3': '点击安装，等待安装完成',
  'detail.guide.android.4': '打开 App，开始使用',
  'detail.guide.desktop': '桌面安装（Chrome / Edge）',
  'detail.guide.desktop.1': '在 Chrome 或 Edge 浏览器中打开在线访问链接',
  'detail.guide.desktop.2': '点击地址栏右侧的安装图标（⊕）',
  'detail.guide.desktop.3': '点击"安装"，App 将作为桌面应用安装',
  'detail.guide.desktop.4': '可从桌面或开始菜单启动，离线可用',
  'detail.guide.ios': 'iPhone 安装（Safari）',
  'detail.guide.ios.1': '在 Safari 中打开在线访问链接',
  'detail.guide.ios.2': '点击底部"分享"按钮（方框向上箭头）',
  'detail.guide.ios.3': '选择"添加到主屏幕"',
  'detail.guide.ios.4': '点击"添加"，App 图标将出现在主屏幕',
  'detail.failed.title': '构建失败',
  'detail.failed.unknown': '未知错误，请查看构建日志或重试。',
  'detail.failed.retry': '修复后重试',
  'detail.failed.retrying': '重试中…',
  'detail.rebuild': '重新构建',
  'detail.rebuilding': '触发中…',
  'detail.idle.hint': '该应用尚未触发构建。',
  'detail.idle.start': '开始构建',
  'detail.idle.starting': '触发中…',
  'detail.info.title': '应用信息',
  'detail.info.name': '名称',
  'detail.info.status': '状态',
  'detail.info.created': '创建时间',
  'detail.info.lastBuild': '最后构建',
  'detail.update.title': '更新应用',
  'detail.update.sub': '上传新的 ZIP 源码包替换当前版本，上传后自动重新构建。',
  'detail.update.dropTitle': '拖拽新版 ZIP 到此处',
  'detail.update.dropSub': '或点击选择文件 · 当前版本将被替换',
  'detail.update.uploading': '上传新版本中…',
  'detail.update.building': '新版本已上传，正在触发构建…',
  'detail.update.busy': '构建进行中，暂不能更新源码',
  'detail.update.rename': '重命名',
  'detail.update.renameSave': '保存',
  'detail.update.renameHint': '修改名称后需重新构建，APK 内的应用名才会更新',
  'detail.update.renameFailed': '重命名失败',
  'detail.danger.title': '危险操作',
  'detail.danger.delete': '删除应用',
  'detail.danger.confirm':
    '确定要删除应用「{name}」吗？此操作将删除 R2 中的所有文件和数据库记录，不可恢复。',
  'detail.danger.deleting': '删除中…',
  'detail.danger.confirmDelete': '确认删除',

  // ---------- 设置 ----------
  'settings.title': '设置',
  'settings.sub': '管理你的账户信息',
  'settings.account.title': '账户信息',
  'settings.account.name': '用户名',
  'settings.account.email': '邮箱',
  'settings.account.plan': '套餐',
  'settings.account.userId': '用户 ID',
  'settings.plan.title': '套餐管理',
  'settings.plan.current': '你当前是',
  'settings.plan.benefits': '权益：3 个应用 · 无水印 · 1GB 存储',
  'settings.plan.freeIntro': '你当前是免费用户。升级到专业版可解锁：',
  'settings.plan.b1': '3 个应用（免费版仅 1 个）',
  'settings.plan.b2': '无水印 APK',
  'settings.plan.b3': '1GB 数据存储',
  'settings.plan.b4': '更多构建次数',
  'settings.plan.upgrade': '升级到专业版',
  'settings.actions.title': '账户操作',
  'settings.actions.logout': '退出登录',
  'settings.language.title': '界面语言',
  'settings.language.hint': '偏好设置会保存在当前浏览器中，立即生效。',

  // ---------- API 令牌 ----------
  'settings.tokens.title': 'API 访问令牌',

  // ---------- 修改密码 ----------
  'settings.password.title': '修改密码',
  'settings.password.intro': '定期更换密码有助于保护账号安全。修改后当前登录保持有效，其他设备下次登录需使用新密码。',
  'settings.password.current': '当前密码',
  'settings.password.new': '新密码',
  'settings.password.newPlaceholder': '8 位以上，含字母和数字',
  'settings.password.confirm': '确认新密码',
  'settings.password.submit': '更新密码',
  'settings.password.submitting': '更新中…',
  'settings.password.success': '密码已更新',
  'settings.password.errMismatch': '两次输入的新密码不一致',
  'settings.tokens.intro': '供 AI Agent、CLI 等自动化工具以你的身份调用 Cloupak API（发布应用、触发构建、下载 APK）。比交出账号密码更安全，可随时撤销。',
  'settings.tokens.create': '创建令牌',
  'settings.tokens.creating': '创建中…',
  'settings.tokens.nameLabel': '令牌名称',
  'settings.tokens.namePlaceholder': '例如：Trae Agent',
  'settings.tokens.empty': '还没有令牌。创建一个，即可让 AI 助手帮你一键发布应用。',
  'settings.tokens.onceHint': '令牌已创建！请立即复制保存（仅显示这一次）：',
  'settings.tokens.copy': '复制',
  'settings.tokens.copied': '已复制',
  'settings.tokens.createdAt': '创建时间',
  'settings.tokens.lastUsed': '最后使用',
  'settings.tokens.never': '从未使用',
  'settings.tokens.revoke': '撤销',
  'settings.tokens.revoking': '撤销中…',
  'settings.tokens.revokeConfirm': '确定撤销该令牌吗？使用它的工具将立即失去访问权限。',
  'settings.tokens.maxReached': '令牌数量已达上限，请先撤销不用的令牌',

  // ---------- 升级 ----------
  'upgrade.pro.title': '套餐管理',
  'upgrade.pro.sub': '你已是专业版用户',
  'upgrade.pro.current': '你当前是',
  'upgrade.pro.enjoy': '享受全部权益',
  'upgrade.pro.back': '返回控制台',
  'upgrade.back': '← 返回设置',
  'upgrade.title': '升级到专业版',
  'upgrade.sub': '解锁更多应用、去除水印、获得更大存储',
  'upgrade.compare.title': '套餐对比',
  'upgrade.compare.feature': '功能',
  'upgrade.compare.free': '免费版',
  'upgrade.compare.pro': '专业版',
  'upgrade.row.apps': '应用数量',
  'upgrade.row.apps.free': '1 个',
  'upgrade.row.apps.pro': '3 个',
  'upgrade.row.apk': 'APK 打包',
  'upgrade.row.sync': '云端数据同步',
  'upgrade.row.qr': '二维码下载',
  'upgrade.row.watermark': 'APK 水印',
  'upgrade.row.watermark.free': '有水印',
  'upgrade.row.watermark.pro': '无水印',
  'upgrade.row.storage': '数据存储',
  'upgrade.row.builds': '每日构建次数',
  'upgrade.row.builds.free': '3 次',
  'upgrade.row.builds.pro': '20 次',
  'upgrade.row.multi': '多设备同步',
  'upgrade.row.price': '价格',
  'upgrade.price.free': '¥0/月',
  'upgrade.price.pro': '¥19/月',
  'upgrade.price.proAlt': '$3/月',
  'upgrade.pay.title': '选择支付方式',
  'upgrade.pay.alipay': '支付宝（¥19/月）',
  'upgrade.pay.qrPlaceholder1': '支付二维码',
  'upgrade.pay.qrPlaceholder2': '（接入后显示）',
  'upgrade.pay.alipayHint': '用支付宝扫码支付 ¥19/月',
  'upgrade.pay.autoNote': '支付完成后自动升级，无需手动操作',
  'upgrade.pay.wxpay': '微信支付（¥19/30天）',
  'upgrade.pay.waffo': '银行卡支付（$3/月）',
  'upgrade.pay.wxpayHint': '用微信扫码支付 ¥19，购买 30 天 Pro',
  'upgrade.pay.xunhupayAlipayHint': '用支付宝扫码支付 ¥19，购买 30 天 Pro',
  'upgrade.pay.xunhupayNote': '一次性购买 30 天，可重复购买顺延时长，到期自动降级',
  'upgrade.pay.waffoNote': '支持 Visa / Mastercard / Apple Pay / Google Pay，自动续费',
  'upgrade.pay.later': '稍后再说',
  'upgrade.notes.title': '说明',
  'upgrade.notes.1': '• 月付，到期后自动降级为免费版（不自动续费）',
  'upgrade.notes.2': '• 两种支付渠道权益完全相同',
  'upgrade.notes.3': '• 升级后 APK 自动重新打包（去除水印）',
  'upgrade.notes.4': '• 支付问题请联系 {email}',

  // ---------- 订阅详情（设置页） ----------
  'billing.paymentMethod': '支付方式',
  'billing.amount': '金额',
  'billing.started': '开始时间',
  'billing.renewsOn': '到期续费',
  'billing.canceledAt': '取消时间',
  'billing.cancelAtEnd': '（到期取消）',
  'billing.cancelSub': '取消订阅',
  'billing.cancelHint': '取消后，当前计费周期结束前你仍可使用全部权益，不会再扣费。',
  'billing.keepSub': '保留订阅',
  'billing.confirmCancel': '确认取消',
  'billing.canceling': '取消中…',
  'billing.noPaymentMethods': '暂未配置支付方式',
  'billing.waitingPayment': '等待支付中…',
  'billing.paymentSuccess': '支付成功！你的套餐已升级。',

  // ---------- 页脚 ----------
  'footer.privacy': '隐私政策',
  'footer.terms': '服务条款',

  // ---------- 法律页面：隐私政策 ----------
  'legal.privacy.title': '隐私政策',
  'legal.privacy.updated': '最后更新：2026 年 9 月',
  'legal.privacy.intro':
    'Cloupak（下称"我们"）尊重并保护你的隐私权。本政策说明我们在你使用 Cloupak 服务时如何收集、使用、存储和共享你的个人信息，以及你依法享有的相关权利。使用本服务即表示你已阅读并同意本政策。',

  'legal.privacy.s1.title': '1. 数据控制者',
  'legal.privacy.s1.p1':
    '本服务由 Cloupak 运营者（依据中国法律经营的个人经营者，下称"我们"）负责运营，是本服务的数据控制者。',
  'legal.privacy.s1.p2':
    '隐私联系邮箱：{email}；数据保护负责人（DPO）：暂未设立。',

  'legal.privacy.s2.title': '2. 我们收集的信息',
  'legal.privacy.s2.p1':
    '你主动提供的信息——账户信息：注册时提交的邮箱地址、用户名和密码（密码仅以哈希形式存储，我们无法查看明文）；应用数据：你上传的项目源码、构建配置，以及应用通过数据同步 SDK 存储的键值数据。',
  'legal.privacy.s2.p2':
    '我们自动收集的信息——设备与网络：IP 地址、浏览器类型、时区；使用行为：构建历史、登录时间、功能使用记录；日志数据：请求时间、错误日志等运营日志，用于保障服务稳定与安全。',
  'legal.privacy.s2.p3':
    '支付信息：仅交易金额与支付状态。我们不存储完整卡号——卡数据由支付处理方（Waffo Pancake 等）直接处理，不经过我们的服务器。',

  'legal.privacy.s3.title': '3. 我们如何使用你的信息',
  'legal.privacy.s3.p1':
    '提供和维护服务、处理计费与支付、提供客户支持（法律依据：合同履行）；发送服务通知（账单、安全、政策更新）与安全欺诈防控（法律依据：合法利益）。',
  'legal.privacy.s3.p2':
    '产品优化与分析：我们可能对数据进行汇总或去标识化处理用于统计分析，此类数据无法识别至具体个人（法律依据：合法利益）。',
  'legal.privacy.s3.p3':
    '法律合规：依适用法律要求处理数据（法律依据：法律义务）。我们目前不发送营销信息。',

  'legal.privacy.s4.title': '4. 本地存储与跟踪技术',
  'legal.privacy.s4.p1':
    '我们在浏览器 localStorage 中仅保存严格必要数据（登录凭证）与功能型数据（语言偏好）。我们不使用分析型或营销型 Cookie，不使用任何第三方分析或跟踪脚本。',

  'legal.privacy.s5.title': '5. 个人信息的共享与披露',
  'legal.privacy.s5.p1':
    '我们不出售你的个人信息，包括适用法律（如 CCPA）所定义的"出售"行为。',
  'legal.privacy.s5.p2':
    '服务提供商：云计算与构建（Cloudflare、GitHub Actions），受数据处理协议约束；支付卡数据由我们符合 PCI-DSS 标准的支付处理方 Waffo Pancake 独家处理，不存储于我们的服务器上；其他支付渠道（支付宝、虎皮椒）同样直接处理支付，我们仅接收交易结果。',
  'legal.privacy.s5.p3':
    '法律与监管要求：依法律、法院命令或主管机关合法请求披露；商业交易：如发生合并、收购，我们将提前通知并确保保护义务延续；经你事先同意的其他用途。',

  'legal.privacy.s6.title': '6. 数据安全措施',
  'legal.privacy.s6.p1':
    '所有网络传输使用 TLS/HTTPS 加密；密码以哈希存储，支付密钥等敏感凭证加密保存；数据按应用与用户严格隔离，遵循最小权限原则。数据存储于 Cloudflare 全球基础设施（Workers、D1、R2、KV）。',
  'legal.privacy.s6.p2':
    '如发生影响你权益的安全事件，我们将在发现后 72 小时内依法通知你及相关监管机构。请妥善保管账户凭据，不要与他人共享。',

  'legal.privacy.s7.title': '7. 个人信息保留期限',
  'legal.privacy.s7.p1':
    '账户信息：账户存续期间保留；注销后 90 天内删除或匿名化。应用数据：应用存续期间保留，删除应用后其源码与关联数据一并删除。',
  'legal.privacy.s7.p2':
    '交易记录：为满足会计与税务要求保留 5 年。客服记录：保留 2 年。安全与运营日志：保留 12 个月。到期后安全删除。',

  'legal.privacy.s8.title': '8. 你的数据权利',
  'legal.privacy.s8.p1':
    '你有权知情、访问、更正、删除你的个人数据，有权限制处理、以机器可读格式获取数据副本（可携带权）、反对基于合法利益的处理。如需行使上述权利，请通过下方邮箱联系我们，我们将在 30 个日历日内处理。',
  'legal.privacy.s8.p2':
    '如认为我们未妥善处理你的请求，你有权向你所在地的数据保护机构投诉。',

  'legal.privacy.s9.title': '9. 跨境数据传输',
  'legal.privacy.s9.p1':
    '我们的服务器及服务提供商（Cloudflare 等）可能位于中国境外。跨境传输时，我们通过签订纳入欧盟标准合同条款（SCC）的数据处理协议、仅向具备同等保护水平的接收方传输等方式保障数据安全。',

  'legal.privacy.s10.title': '10. 未成年人保护',
  'legal.privacy.s10.p1':
    '本服务面向年满 18 周岁的用户。我们不会故意收集低于该年龄未成年人的信息。如你认为未成年人向我们提供了信息，请立即联系我们，我们将尽快删除。',

  'legal.privacy.s11.title': '11. 政策变更',
  'legal.privacy.s11.p1':
    '如发生重大变更，我们将至少提前 15 天通过站内公告或你的注册邮箱通知你，并更新本页"最后更新"日期。生效日后继续使用即视为接受。',

  'legal.privacy.s12.title': '12. 联系我们',
  'legal.privacy.s12.p1': '任何与隐私相关的问题或数据权利请求，请联系：{email}',

  // ---------- 法律页面：服务条款 ----------
  'legal.terms.title': '服务条款',
  'legal.terms.updated': '最后更新：2026 年 9 月',
  'legal.terms.intro':
    '欢迎使用 Cloupak。本服务由 Cloupak 运营者（依据中国法律经营的个人经营者）提供。使用本服务即表示你同意以下条款，请仔细阅读。',

  'legal.terms.s1.title': '1. 条款接受',
  'legal.terms.s1.p1':
    '注册账户、订阅方案或以其他方式使用本服务，即表示你：（a）已年满 18 周岁；（b）已阅读、理解并同意受本条款约束；（c）同意我们的隐私政策；且（d）有权代表自身或所属机构签署本协议。',
  'legal.terms.s1.p2':
    '重要提示：如不同意本条款，请停止使用本服务。条款更新后继续使用即表示接受修订后的条款。',

  'legal.terms.s2.title': '2. 服务描述',
  'legal.terms.s2.p1':
    'Cloupak 提供 Web 项目到 Android 应用的自动打包与云端托管服务，包括自动部署、数据同步、APK 构建与分发，属于通过互联网提供的数字软件即服务（SaaS）产品。',
  'legal.terms.s2.p2':
    '本服务为数字无形产品，付款确认后即时开通。由于数字服务的即时交付性质，退款适用特定限制，详见第 7 条。',

  'legal.terms.s3.title': '3. 账户',
  'legal.terms.s3.p1':
    '你需要使用准确、完整的信息注册账户，并妥善保管登录凭据；账户下的所有操作均视为你本人行为，不得将凭据共享给任何第三方。',
  'legal.terms.s3.p2':
    '请确保账单邮箱地址持续有效，以便接收收据与订阅通知。如代表公司或组织注册，你声明已获授权代表该实体签署本协议。',

  'legal.terms.s4.title': '4. 套餐与定价',
  'legal.terms.s4.p1':
    '免费版提供基础额度；专业版按月付费（¥19/月或 $3/月，按支付渠道而定）。当前方案详情和定价请访问网站定价页。我们保留随时调整方案功能的权利，价格变更遵循第 5 条。',

  'legal.terms.s5.title': '5. 计费与付款',
  'legal.terms.s5.p1':
    '自动续费授权：你通过银行卡渠道订阅付费方案时，即明确授权 Cloupak 按计费周期向你的支付方式扣收订阅费用，该授权持续有效直至你按第 6 条取消。订阅在每个计费周期结束时自动续费，除非你取消。',
  'legal.terms.s5.p2':
    '国内渠道（支付宝/微信）为一次性购买 30 天时长，不自动扣款，可重复购买顺延。',
  'legal.terms.s5.p3':
    '税费：除非另有说明，所列价格均不含适用税费；如适用法律要求，我们将代收代缴相关税费。',
  'legal.terms.s5.p4':
    '支付处理：银行卡付款由我们符合 PCI-DSS 标准的支付处理方 Waffo Pancake 处理，卡数据不存储于我们的服务器；支付宝/微信付款由相应渠道直接处理，我们仅接收交易结果。',

  'legal.terms.s6.title': '6. 取消政策',
  'legal.terms.s6.p1':
    '你可随时通过以下任一方式取消订阅：在线（推荐）——登录后进入「设置 → 订阅 → 取消订阅」；或从注册邮箱发送取消申请至 {email}。取消立即登记，当前计费周期到期前服务持续有效，此后不再扣款。',
  'legal.terms.s6.p2':
    '取消后，账户数据保留 90 天，随后依据隐私政策删除。你可随时联系我们注销账户。',

  'legal.terms.s7.title': '7. 退款政策',
  'legal.terms.s7.p1':
    '由于本服务为即时交付的数字无形产品，订阅费用原则上不予退款，但以下情形除外。',
  'legal.terms.s7.p2':
    '可退款情形：（a）重复扣款——因计费错误导致的重复扣款，全额退款；（b）服务中断——因我方原因导致的服务中断超过 72 小时，按比例退款或展期；（c）法定权利——如适用法律赋予撤销权或退款权（如欧盟/英国 14 天冷静期），该等权利予以保留。',
  'legal.terms.s7.p3':
    '新订户 7 日保障：首次订阅的用户可在首次扣款后 7 天内申请全额退款（前提是用量未超过方案配额的 20%）。',
  'legal.terms.s7.p4':
    '申请方式：发送邮件至 {email}，注明账户邮箱、交易 ID 及退款原因。我们将在 2 个工作日内确认，并在 5–10 个工作日内原路退回。',

  'legal.terms.s8.title': '8. 账单争议',
  'legal.terms.s8.p1':
    '如你认为扣款存在错误，请先发邮件至 {email}，在向银行提起交易争议前与我们联系。我们承诺在 2 个工作日内回复，并在 5 个工作日内解决确认的计费错误。',

  'legal.terms.s9.title': '9. 用户内容与知识产权',
  'legal.terms.s9.p1':
    '你上传的代码与应用数据归你所有。为提供构建与托管服务，你授予我们必要的临时处理许可，除此之外我们不会将你的内容用于其他用途。你需保证上传内容合法合规，不侵犯任何第三方权益。',
  'legal.terms.s9.p2':
    'Cloupak 平台（网站、构建系统、数据同步 SDK 等）的知识产权归我们所有，未经许可不得复制、转售或用于商业用途。',

  'legal.terms.s10.title': '10. 可接受使用',
  'legal.terms.s10.p1':
    '禁止利用本服务：制作或传播恶意软件、钓鱼内容或网络攻击工具；传播侵权、违法、诽谤或欺诈性内容；制作深度伪造内容仿冒真实个人；爬取或滥用服务；绕过或干扰服务安全功能；未经批准转售访问权；或违反任何适用法律与卡组规则。',
  'legal.terms.s10.p2': '违反上述规定的，我们有权暂停或终止对你的服务。',

  'legal.terms.s11.title': '11. 数据、隐私与安全',
  'legal.terms.s11.p1':
    '你对本服务的使用受我们隐私政策的约束，隐私政策发布于 cloupak.app/privacy。支付卡数据由符合 PCI-DSS 标准的支付处理方 Waffo Pancake 独家处理，不存储于我们的服务器上。账户数据在取消订阅后保留 90 天，随后删除。',

  'legal.terms.s12.title': '12. 免责声明与责任限制',
  'legal.terms.s12.p1':
    '本服务以"现状"和"如可用"的方式提供，不作任何明示或隐含的保证，包括适销性或特定用途适用性。我们不保证服务绝对不间断或无错误，你应自行备份源码等重要数据。',
  'legal.terms.s12.p2':
    '在适用法律允许的最大范围内，我们对任何间接、附带或后果性损害不承担责任；累计赔偿责任以你在索赔事件发生前 12 个月内向我们实际支付的金额为限。',

  'legal.terms.s13.title': '13. 期限与终止',
  'legal.terms.s13.p1':
    '本条款在你使用本服务期间持续有效。如你严重违反本条款、涉嫌欺诈或法律要求，我们可暂停或终止你的账户。如我方因非你违约原因终止服务，将按比例退还预付未使用部分。你可随时通过联系我们删除账户。',

  'legal.terms.s14.title': '14. 条款变更',
  'legal.terms.s14.p1':
    '我们可随时更新本条款，重大变更将在生效日前至少 14 天通过邮件或站内公告通知。继续使用即表示接受。如任何条款被认定无效，其余条款继续完全有效。本条款与隐私政策共同构成你与我们之间的完整协议。',

  'legal.terms.s15.title': '15. 适用法律与争议解决',
  'legal.terms.s15.p1':
    '本条款受中华人民共和国法律管辖。提起正式程序前，请先发邮件至 {email} 尝试协商解决。',

  'legal.terms.s16.title': '16. 联系方式',
  'legal.terms.s16.p1':
    '客服、账单、退款、取消与法务事宜均可联系：{email}。使用本服务或在结账时同意本条款，即表示你已阅读并同意本条款。',
};

/** 全部翻译键（以 zh 词典键集合为准） */
export type TranslationKey = keyof typeof zh;

export const en: Record<TranslationKey, TranslationValue> = {
  // ---------- Language switch ----------
  'lang.switch': 'Switch language',
  'lang.zh': '中文',
  'lang.en': 'English',

  // ---------- Common ----------
  'common.loading': 'Loading…',
  'common.retry': 'Retry',
  'common.cancel': 'Cancel',
  'common.reset': 'Reset',
  'common.remove': 'Remove',
  'common.refreshing': 'Refreshing…',
  'common.backToApps': 'Back to apps',
  'common.loadFailed': 'Failed to load: {message}',
  'common.appCount': { one: '{count} app', other: '{count} apps' },

  // ---------- Plans ----------
  'plan.free': 'Free',
  'plan.pro': 'Pro',
  'plan.enterprise': 'Enterprise',

  // ---------- Shell (topbar / footer / 404) ----------
  'app.brandAria': 'Cloupak home',
  'app.navAria': 'Main navigation',
  'app.footerTagline': 'One click to the cloud',
  'nav.apps': 'My Apps',
  'nav.create': 'New App',
  'nav.settings': 'Settings',
  'nav.logout': 'Sign out',
  'nav.login': 'Sign in',
  'notFound.title': 'Page not found',
  'notFound.desc': 'The page you are looking for does not exist or has been moved.',
  'notFound.back': 'Back to apps',

  // ---------- Build status ----------
  'status.idle': 'Idle',
  'status.uploading': 'Uploading',
  'status.building': 'Building',
  'status.success': 'Ready',
  'status.failed': 'Failed',

  // ---------- Relative time ----------
  'relTime.justNow': 'just now',
  'relTime.minutes': '{n} min ago',
  'relTime.hours': '{n} h ago',
  'relTime.days': '{n} d ago',

  // ---------- API error fallbacks ----------
  'apiError.requestFailed': 'Request failed (HTTP {status})',
  'apiError.downloadFailed': 'Download failed (HTTP {status})',
  'apiError.code.LOGIN_FAILED': 'Login failed',
  'apiError.code.EMAIL_ALREADY_EXISTS': 'This email is already registered',
  'apiError.code.NO_PASSWORD_SET': 'This account has no password set. Please sign in with OAuth.',
  'apiError.code.WEAK_PASSWORD': 'Password is too weak: 8–64 characters with both letters and numbers required',
  'apiError.code.INVALID_TOKEN': 'Authentication failed. Please sign in again.',
  'apiError.code.TOKEN_EXPIRED': 'Your session has expired. Please sign in again.',
  'apiError.code.MISSING_AUTH': 'Please sign in first',
  'apiError.code.CREATE_USER_FAILED': 'Failed to create user',
  'apiError.code.INVALID_EMAIL': 'Invalid email address',
  'apiError.code.MISSING_FIELDS': 'Please fill in all required fields',
  'apiError.code.INVALID_CREDENTIALS': 'Incorrect email or password',
  'apiError.code.USER_BANNED': 'This account has been banned',
  'apiError.code.REGISTER_FAILED': 'Registration failed',
  'apiError.code.UPLOAD_FAILED': 'Upload failed',
  'apiError.code.APK_NOT_FOUND': 'APK not ready yet. Please retry later or rebuild.',
  'apiError.code.APK_DOWNLOAD_FAILED': 'APK download failed',
  'apiError.code.QUERY_FAILED': 'Query failed',
  'apiError.code.CREATE_APP_FAILED': 'Failed to create app',
  'apiError.code.INVALID_FILE_TYPE': 'Only ZIP files are supported',
  'apiError.code.DELETE_FAILED': 'Delete failed',
  'apiError.code.NO_FILE': 'No file provided or invalid file format',
  'apiError.code.FILE_TOO_LARGE': 'File exceeds the 50MB limit',
  'apiError.code.APP_NOT_FOUND': 'App not found or access denied',
  'apiError.code.INVALID_PLAN': 'Invalid plan',
  'apiError.code.ALREADY_SUBSCRIBED': 'You are already on this plan',
  'apiError.code.INVALID_BODY': 'Invalid request',
  'apiError.code.ALIPAY_NOT_CONFIGURED': 'Alipay is not configured',
  'apiError.code.XUNHUPAY_NOT_CONFIGURED': 'This payment channel is not configured',
  'apiError.code.WAFFO_NOT_CONFIGURED': 'This payment method is not configured',
  'apiError.code.ALIPAY_ERROR': 'Failed to create order',
  'apiError.code.XUNHUPAY_ERROR': 'Failed to create order',
  'apiError.code.WAFFO_ERROR': 'Failed to create checkout session',
  'apiError.code.CANCEL_FAILED': 'Cancellation failed',
  'apiError.code.NO_ACTIVE_SUB': 'No active subscription',
  'apiError.code.NOT_FOUND': 'Record not found',
  'apiError.code.QUOTA_EXCEEDED': 'Build quota exceeded',
  'apiError.code.TRIGGER_FAILED': 'Failed to trigger build',
  'apiError.code.BUILD_ERROR': 'Failed to trigger build',
  'apiError.code.NO_SOURCE': 'No uploaded source found. Please upload a ZIP first.',
  'apiError.code.BUILD_IN_PROGRESS': 'A build is in progress. Please wait for it to finish before updating.',
  'apiError.code.INVALID_NAME': 'App name cannot be empty',
  'apiError.code.NAME_TOO_LONG': 'App name cannot exceed 40 characters',
  'apiError.code.UPDATE_APP_FAILED': 'Failed to update app',
  'apiError.code.INTERNAL_ERROR': 'Internal server error',

  // ---------- Landing ----------
  'landing.hero.titleA': 'Finish your code, ',
  'landing.hero.titleB': 'one tap',
  'landing.hero.titleC': ' to a mobile app.',
  'landing.hero.sub':
    'Turn your web app into an installable APK — auto-deployed to the cloud, database auto-configured, packaged automatically. Zero deployment code.',
  'landing.hero.ctaConsole': 'Go to console',
  'landing.hero.ctaStart': 'Start free',
  'landing.hero.ctaHow': 'How it works',
  'landing.hero.trust':
    'Email sign-in · No company required · No ICP filing · No Android toolchain',

  'landing.flow1.label': 'Upload a folder or paste code',
  'landing.flow1.detail': 'Drop a ZIP / paste HTML',
  'landing.flow2.label': 'Auto deploy + package',
  'landing.flow2.detail': 'R2 + Cloudflare + Gradle',
  'landing.flow3.label': 'Scan to install on your phone',
  'landing.flow3.detail': 'APK download + QR code',

  'landing.how.title': 'Three steps to the cloud',
  'landing.how.sub': 'From code to a phone app, fully automated',
  'landing.step1.title': 'Upload your app',
  'landing.step1.desc':
    'Drag & drop a folder, or paste code. Works with React, Vue, plain HTML and every web framework — the project type is detected automatically.',
  'landing.step2.title': 'Auto-deploy & package',
  'landing.step2.desc':
    'We deploy to Cloudflare, wire up data sync, and build an APK with Gradle. Done in about 5 minutes.',
  'landing.step3.title': 'Scan & install',
  'landing.step3.desc':
    'Scan the QR code with your phone to download and install the APK. Changed code? Hit "Update" and it redeploys automatically.',

  'landing.features.title': 'Why Cloupak',
  'landing.features.sub': 'More than a packager — a complete cloud hosting solution',
  'landing.feat1.title': 'Zero-config packaging',
  'landing.feat1.desc':
    'No Android Studio, no Gradle setup, no signing knowledge. Upload code, get an APK.',
  'landing.feat2.title': 'Cloud data hosting',
  'landing.feat2.desc':
    'A data-sync SDK is wired in automatically. Your app data lives safely in the cloud and syncs across devices.',
  'landing.feat3.title': 'Auto-update on code change',
  'landing.feat3.desc':
    'WebView technology keeps installed apps always up to date. Push code, hit "Update" — no APK re-download needed.',
  'landing.feat4.title': 'Native Android support',
  'landing.feat4.desc':
    'Beyond web projects, native Android Kotlin/Java projects are supported too, with the sync SDK injected automatically.',
  'landing.feat5.title': 'Done in 5 minutes',
  'landing.feat5.desc':
    'From code submission to a downloadable APK, the whole flow stays under 10 minutes at P95. No review queues.',
  'landing.feat6.title': 'Your own personal app',
  'landing.feat6.desc':
    'A to-do list for your family, a budget tracker for yourself — install your own apps without an app store.',

  'landing.pricing.title': 'Simple pricing',
  'landing.pricing.sub': 'Start free, pay when it works for you',
  'landing.pricing.tierFree': 'Free',
  'landing.pricing.tierPersonal': 'Personal',
  'landing.pricing.freeAmount': '$0',
  'landing.pricing.personalAmount': '$3',
  'landing.pricing.personalAlt': ' / ¥19/mo',
  'landing.pricing.perMonth': '/mo',
  'landing.price.f1': '1 app',
  'landing.price.f1Personal': '3 apps',
  'landing.price.f2': 'Auto deploy + APK build',
  'landing.price.f3': 'Cloud data sync',
  'landing.price.f4': 'QR code download',
  'landing.price.f5': 'No watermark',
  'landing.price.f6': 'Multi-device sync',
  'landing.price.f7': '1GB data storage',
  'landing.pricing.upgradePersonal': 'Upgrade to Personal',
  'landing.pricing.note':
    'Alipay/WeChat QR (¥19/mo) in China · Card payment ($3/mo) worldwide · Same benefits',

  'landing.cta.title': 'Ready to try it?',
  'landing.cta.sub': 'Create your first app in 30 seconds · Email sign-in supported',
  'landing.footer': 'Cloupak — everyone deserves their own mobile app · 2026',

  // ---------- Auth ----------
  'auth.tabLogin': 'Sign in',
  'auth.tabRegister': 'Sign up',
  'auth.loginTitle': 'Welcome back',
  'auth.registerTitle': 'Create an account',
  'auth.loginSub': 'Sign in to manage your Cloupak apps',
  'auth.registerSub': 'Sign up to start building and deploying apps',
  'auth.name': 'Username',
  'auth.email': 'Email',
  'auth.password': 'Password',
  'auth.namePlaceholder': 'Your name',
  'auth.passwordPlaceholder': '8+ characters with letters and numbers',
  'auth.submitting': 'Processing…',
  'auth.submitLogin': 'Sign in',
  'auth.submitRegister': 'Sign up',
  'auth.errName': 'Please enter a username',
  'auth.errEmail': 'Please enter a valid email address',
  'auth.errPasswordRequired': 'Please enter your password',
  'auth.pwdErrLength': 'Password must be 8–64 characters long',
  'auth.pwdErrChars': 'Password must contain both letters and numbers',
  'auth.pwdHint': '8–64 characters, must contain both letters and numbers',
  'auth.errFailed': 'Something went wrong, please try again.',
  'auth.captchaLoadFailed': 'Failed to load the human verification widget. Please check your network and refresh (some proxies or ad blockers may block the Cloudflare verification service).',
  'auth.captchaPreparing': 'Preparing human verification…',
  'auth.captchaSlow': 'Verification is responding slowly, retrying automatically…',
  'auth.forgotLink': 'Forgot password?',
  'auth.forgotTitle': 'Reset your password',
  'auth.forgotSub': 'Enter your account email and we will send you a reset link',
  'auth.forgotSubmit': 'Send reset email',
  'auth.forgotSent': 'If that email is registered, a reset link has been sent. Please reset within 30 minutes (check your spam folder).',
  'auth.backToLogin': 'Back to login',
  'auth.resetTitle': 'Set a new password',
  'auth.resetSub': 'Set a new password for your account',
  'auth.resetSubmit': 'Reset and sign in',
  'auth.resetInvalidLink': 'This reset link is invalid or has expired. Please request a new one.',
  'auth.resetReRequest': 'Send another reset email',

  // ---------- Dashboard ----------
  "workspace.eyebrow": "YOUR WORKSPACE",
  "workspace.dashboardSub": "Publish, update, and share your apps from one place.",
  "workspace.publishApp": "Publish app",
  "workspace.overview": "Workspace overview",
  "workspace.introTitle": "Your projects, ready for everyday use.",
  "workspace.introSub": "Upload your project. We build it. You open it on your phone or in a browser.",
  "workspace.live": "Live",
  "workspace.inProgress": "Building",
  "workspace.appLibrary": "App library",
  "workspace.libraryHint": "All your published projects",
  "workspace.emptyTitle": "Give your first project a home",
  "workspace.emptyHint": "Static websites, frontend projects, and Android projects welcome",
  "workspace.agentHint": "Building with an AI assistant? Let it publish your project for you.",
  "workspace.connectAgent": "Connect your assistant",
  "workspace.cardReady": "Ready to install on your phone or open online.",
  "workspace.cardFailed": "This build needs attention. Review the details to continue.",
  "workspace.cardDraft": "Your project is set up. Add your source files to get started.",
  "workspace.cardBuilding": "Your project is being built in the cloud.",
  "workspace.updated": "Updated {time}",
  "workspace.openApp": "Open app",
  "workspace.manageApp": "Download & manage",
  "workspace.resolveBuild": "Review build",
  "workspace.finishSetup": "Continue setup",
  "workspace.viewBuild": "View build",
  "workspace.fileEmpty": "This ZIP is empty. Choose an archive containing your project files.",
  "workspace.fileLarge": "This file exceeds 50 MB. Remove dependencies and generated files, then zip it again.",
  "workspace.changeFile": "Change file",
  "workspace.uploadOr": "or select a file from your computer",
  "workspace.chooseFile": "Choose ZIP file",
  "workspace.publishJourney": "Publishing steps",
  "workspace.stepPrepare": "Prepare project",
  "workspace.stepBuild": "Build in the cloud",
  "workspace.stepInstall": "Install & share",
  "workspace.publishEyebrow": "LET’S MAKE IT LIVE",
  "workspace.publishTitle": "Publish your app",
  "workspace.publishSub": "Give your app a name and upload your project. Cloupak takes care of the rest.",
  "workspace.projectDetails": "Project details",
  "workspace.requiredFields": "Both fields are required",
  "workspace.nameExample": "For example, My spending tracker",
  "workspace.publishError": "We couldn’t finish publishing",
  "workspace.retryHint": "Your project details are saved. Check your file and try again.",
  "workspace.retryPublish": "Try publishing again",
  "workspace.buildInCloud": "No build tools to install",
  "workspace.zipHelpTitle": "Prepare your project ZIP",
  "workspace.zipHelpSub": "Put your project files in a ZIP archive. Before uploading:",
  "workspace.zipHelp1": "Include index.html for a website, package.json for a frontend app, or your Gradle files for Android.",
  "workspace.zipHelp2": "Leave out node_modules, .git, and generated files. Keep the ZIP under 50 MB.",
  "workspace.zipHelp3": "Leave out passwords, API keys, and .env files. Frontend builds should output a dist or build folder.",
  "workspace.supported": "Supported projects",
  "workspace.whatNext": "What happens next?",
  "workspace.whatNextSub": "Cloupak builds your project in the cloud. When it’s ready, download the Android installer. Web projects also get a live link.",
  "workspace.appOverview": "APP OVERVIEW",
  "workspace.buildFailedTitle": "This build needs attention",
  "workspace.buildFailedSub": "Review the build details below. Upload a corrected project or retry your current source.",
  "workspace.draftTitle": "Your project is set up",
  "workspace.draftSub": "Upload your project using Update app below, then start a build.",
  "workspace.buildDetails": "Build details",
  "workspace.technicalLabel": "Logs & history",
  "workspace.buildHistory": "Recent builds",
  "workspace.noBuilds": "No builds yet.",
  "workspace.appId": "App ID",
  "workspace.shareTitle": "Share your app",
  "workspace.shareSub": "Share the live link for web projects. Android users can also scan the QR code to get the installer.",
  "workspace.deleteConfirm": "Delete “{name}”? The app, its files, and cloud data will be permanently removed. This cannot be undone.",
  "workspace.cloudBuild": "BUILDING IN THE CLOUD",
  "workspace.buildingTitle": "Getting your app ready",
  "workspace.buildingSub": "Your project is being built and packaged in the cloud. The result will appear here automatically.",
  "workspace.buildingHint": "You can leave this page and check back from your app library.",
  "workspace.readyEyebrow": "BUILD COMPLETE",
  "workspace.readyTitle": "Your app is ready",
  "workspace.readySub": "Download it to your Android phone, or open it in your browser.",
  "workspace.downloadAndroid": "Download Android app",
  "workspace.scanPhone": "Scan on your phone",
  "workspace.androidOnly": "Android installer",
  "workspace.qrUnavailable": "QR code unavailable. Use the download button.",
  "workspace.copyLink": "Copy app link",
  "workspace.copied": "Link copied",
  "workspace.copyFallback": "Select the link above and copy it manually.",
  "workspace.installHelp": "Need help installing?",
  "workspace.newVersion": "Upload new project files",
  "workspace.publishUpdate": "Publish update",
  "workspace.billingNav": "Plan & billing",
  "workspace.accountNav": "Account",
  "workspace.securityNav": "Security",
  "workspace.integrationsNav": "AI assistants",
  "workspace.settingsSub": "Manage your account, plan, and connections to your AI assistant.",
  "workspace.integrationSub": "Create an access token so your AI assistant can publish and update apps for you.",
  "workspace.landingEyebrow": "BRING YOUR PROJECT TO LIFE",
  "workspace.landingTitle": "From your project to your phone.",
  "workspace.landingSub": "Turn your web or Android project into an installable app. Upload a ZIP. Cloupak handles the cloud build, packaging, and publishing.",
  "workspace.landingProof": "Web & Android projects · No local build tools",
  "workspace.landingPreview": "PRODUCT PREVIEW · EXAMPLE PROJECT",
  "workspace.landingExample": "Daily spending",
  "workspace.landingExampleSub": "Your ideas, ready to use anywhere.",
  "workspace.landingPreviewReady": "Ready to install",
  "workspace.landingWorkflow": "From project to app in three steps",
  "workspace.landingWorkflowSub": "Focus on creating. We take care of publishing.",
  "workspace.landingFeatureTitle": "More possibilities for your project",
  "workspace.landingFeatureSub": "Builds, hosting, and data sync in one place.",
  "workspace.landingCtaTitle": "Your next app starts here.",
  "workspace.landingCtaSub": "Upload your project and see it running on your phone.",
  'workspace.skipContent': 'Skip to content',
  "workspace.landingPrepare": "Name your app, zip your project, and upload it. Static websites, frontend apps, and Android projects are supported.",
  "workspace.landingBuild": "Cloupak identifies and builds your project in the cloud. Check the result from your workspace.",
  "workspace.landingInstall": "Download the Android installer. Web projects also get a live link you can share.",
  "workspace.featurePackage": "Cloud packaging",
  "workspace.featurePackageSub": "Get an Android installer from a supported project, without setting up build or signing tools on your computer.",
  "workspace.featureSync": "Data that travels with you",
  "workspace.featureSyncSub": "For web apps that save data in the browser, Cloupak adds cloud sync so you can use them across devices.",
  "workspace.featureUpdate": "Simple updates",
  "workspace.featureUpdateSub": "Upload a new ZIP when your project changes. Web content updates at the same link; native Android updates use a new APK.",
  'workspace.accountDetails': 'Account identifiers',
  'workspace.viewPlans': 'View plans',
  'dashboard.title': 'My Apps',
  'dashboard.sub': 'Manage your Cloupak apps and builds',
  'dashboard.create': 'New App',
  'dashboard.emptyTitle': 'No apps yet',
  'dashboard.emptyDesc': 'Create your first app — upload a ZIP to start building.',
  'dashboard.createFirst': 'Create your first app',

  // ---------- Create app ----------
  'create.stage.creating': 'Creating app…',
  'create.stage.uploading': 'Uploading ZIP…',
  'create.stage.building': 'Triggering build…',
  'create.stage.done': 'Done, redirecting…',
  'create.stage.error': 'Something went wrong',
  'create.title': 'Create a new app',
  'create.sub': 'Upload your project ZIP — Cloupak handles the build and packaging.',
  'create.nameLabel': 'App name',
  'create.namePlaceholder': 'e.g. my-awesome-app',
  'create.nameHint': 'Shown under your app icon (max 40 characters).',
  'create.zipLabel': 'Upload project ZIP',
  'create.errZipOnly': 'Only .zip files are supported',
  'create.dropTitle': 'Drop your ZIP here',
  'create.dropSub': 'or click to browse',
  'create.dropHint': '.zip only · 50MB max',
  'create.submitFailed': 'Submission failed, please try again.',
  'create.uploadAndBuild': 'Upload & build',

  // ---------- App detail ----------
  'detail.deleteFailed': 'Delete failed',
  'detail.notFound': 'App not found.',
  'detail.cardProgress': 'Deploy progress',
  'detail.cardDone': 'Deploy complete',
  'detail.cardFailed': 'Deploy failed',
  'detail.cardIdle': 'App status',
  'detail.building': 'Building…',
  'detail.buildingHint': 'Building — this page refreshes the status every 3 seconds…',
  'detail.liveUrl': 'Live URL',
  'detail.downloadApk': 'Download APK',
  'detail.downloading': 'Downloading…',
  'detail.retryDownload': 'Retry download',
  'detail.clickToDownload': 'Click to download APK',
  'detail.downloadFailed': 'Download failed',
  'detail.scanTitle': 'Scan to download APK',
  'detail.scanAlt': 'APK download QR code',
  'detail.scanHint': 'Scan with an Android phone to download and install directly',
  'detail.guide.android': 'Install on Android',
  'detail.guide.android.1': 'Tap "Download APK" above, or scan the QR code to get the installer',
  'detail.guide.android.2': 'When asked to allow installs from unknown sources, tap Allow',
  'detail.guide.android.3': 'Tap Install and wait for it to finish',
  'detail.guide.android.4': 'Open the app and you are done',
  'detail.guide.desktop': 'Install on desktop (Chrome / Edge)',
  'detail.guide.desktop.1': 'Open the live URL in Chrome or Edge',
  'detail.guide.desktop.2': 'Click the install icon (⊕) at the right end of the address bar',
  'detail.guide.desktop.3': 'Click "Install" — the app installs as a desktop app',
  'detail.guide.desktop.4': 'Launch it from the desktop or Start menu, works offline',
  'detail.guide.ios': 'Install on iPhone (Safari)',
  'detail.guide.ios.1': 'Open the live URL in Safari',
  'detail.guide.ios.2': 'Tap the Share button (square with an up arrow)',
  'detail.guide.ios.3': 'Choose "Add to Home Screen"',
  'detail.guide.ios.4': 'Tap "Add" — the app icon appears on your home screen',
  'detail.failed.title': 'Build failed',
  'detail.failed.unknown': 'Unknown error — check the build log or retry.',
  'detail.failed.retry': 'Retry after fixing',
  'detail.failed.retrying': 'Retrying…',
  'detail.rebuild': 'Rebuild',
  'detail.rebuilding': 'Starting…',
  'detail.idle.hint': 'This app has not been built yet.',
  'detail.idle.start': 'Start build',
  'detail.idle.starting': 'Starting…',
  'detail.info.title': 'App info',
  'detail.info.name': 'Name',
  'detail.info.status': 'Status',
  'detail.info.created': 'Created',
  'detail.info.lastBuild': 'Last build',
  'detail.update.title': 'Update app',
  'detail.update.sub': 'Upload a new ZIP source package to replace the current version. A rebuild starts automatically after upload.',
  'detail.update.dropTitle': 'Drop the new ZIP here',
  'detail.update.dropSub': 'or click to browse · the current version will be replaced',
  'detail.update.uploading': 'Uploading new version…',
  'detail.update.building': 'New version uploaded, triggering build…',
  'detail.update.busy': 'A build is in progress — source update is disabled',
  'detail.update.rename': 'Rename',
  'detail.update.renameSave': 'Save',
  'detail.update.renameHint': 'After renaming, rebuild to update the app name inside the APK',
  'detail.update.renameFailed': 'Rename failed',
  'detail.danger.title': 'Danger zone',
  'detail.danger.delete': 'Delete app',
  'detail.danger.confirm':
    'Delete "{name}"? This permanently removes all files in R2 and all database records. This cannot be undone.',
  'detail.danger.deleting': 'Deleting…',
  'detail.danger.confirmDelete': 'Confirm delete',

  // ---------- Settings ----------
  'settings.title': 'Settings',
  'settings.sub': 'Manage your account',
  'settings.account.title': 'Account',
  'settings.account.name': 'Username',
  'settings.account.email': 'Email',
  'settings.account.plan': 'Plan',
  'settings.account.userId': 'User ID',
  'settings.plan.title': 'Plan',
  'settings.plan.current': 'You are currently on',
  'settings.plan.benefits': 'Benefits: 3 apps · No watermark · 1GB storage',
  'settings.plan.freeIntro': 'You are on the free plan. Upgrade to Pro to unlock:',
  'settings.plan.b1': '3 apps (free plan: 1)',
  'settings.plan.b2': 'Watermark-free APK',
  'settings.plan.b3': '1GB data storage',
  'settings.plan.b4': 'More builds per day',
  'settings.plan.upgrade': 'Upgrade to Pro',
  'settings.actions.title': 'Account actions',
  'settings.actions.logout': 'Sign out',
  'settings.language.title': 'Language',
  'settings.language.hint': 'Your preference is saved in this browser and applies immediately.',

  // ---------- API tokens ----------
  'settings.tokens.title': 'API Access Tokens',

  // ---------- Change password ----------
  'settings.password.title': 'Change Password',
  'settings.password.intro': 'Changing your password regularly keeps your account secure. Your current session stays signed in; other devices will need the new password next time.',
  'settings.password.current': 'Current password',
  'settings.password.new': 'New password',
  'settings.password.newPlaceholder': '8+ characters with letters and numbers',
  'settings.password.confirm': 'Confirm new password',
  'settings.password.submit': 'Update password',
  'settings.password.submitting': 'Updating…',
  'settings.password.success': 'Password updated',
  'settings.password.errMismatch': 'The two new passwords do not match',
  'settings.tokens.intro': 'Let AI agents, CLIs and other automation tools call the Cloupak API on your behalf (publish apps, trigger builds, download APKs). Safer than sharing your password, and revocable anytime.',
  'settings.tokens.create': 'Create Token',
  'settings.tokens.creating': 'Creating…',
  'settings.tokens.nameLabel': 'Token name',
  'settings.tokens.namePlaceholder': 'e.g. Trae Agent',
  'settings.tokens.empty': 'No tokens yet. Create one so your AI assistant can publish apps for you in one step.',
  'settings.tokens.onceHint': 'Token created! Copy it now — it is shown only once:',
  'settings.tokens.copy': 'Copy',
  'settings.tokens.copied': 'Copied',
  'settings.tokens.createdAt': 'Created',
  'settings.tokens.lastUsed': 'Last used',
  'settings.tokens.never': 'Never used',
  'settings.tokens.revoke': 'Revoke',
  'settings.tokens.revoking': 'Revoking…',
  'settings.tokens.revokeConfirm': 'Revoke this token? Tools using it will lose access immediately.',
  'settings.tokens.maxReached': 'Token limit reached. Revoke unused tokens first.',

  // ---------- Upgrade ----------
  'upgrade.pro.title': 'Plan',
  'upgrade.pro.sub': 'You are already on Pro',
  'upgrade.pro.current': 'You are currently on',
  'upgrade.pro.enjoy': 'enjoying all benefits',
  'upgrade.pro.back': 'Back to console',
  'upgrade.back': '← Back to settings',
  'upgrade.title': 'Upgrade to Pro',
  'upgrade.sub': 'More apps, no watermark, more storage',
  'upgrade.compare.title': 'Plan comparison',
  'upgrade.compare.feature': 'Feature',
  'upgrade.compare.free': 'Free',
  'upgrade.compare.pro': 'Pro',
  'upgrade.row.apps': 'Apps',
  'upgrade.row.apps.free': '1',
  'upgrade.row.apps.pro': '3',
  'upgrade.row.apk': 'APK build',
  'upgrade.row.sync': 'Cloud data sync',
  'upgrade.row.qr': 'QR code download',
  'upgrade.row.watermark': 'APK watermark',
  'upgrade.row.watermark.free': 'Watermarked',
  'upgrade.row.watermark.pro': 'None',
  'upgrade.row.storage': 'Data storage',
  'upgrade.row.builds': 'Builds per day',
  'upgrade.row.builds.free': '3',
  'upgrade.row.builds.pro': '20',
  'upgrade.row.multi': 'Multi-device sync',
  'upgrade.row.price': 'Price',
  'upgrade.price.free': '$0/mo',
  'upgrade.price.pro': '$3/mo',
  'upgrade.price.proAlt': '¥19/mo',
  'upgrade.pay.title': 'Choose a payment method',
  'upgrade.pay.alipay': 'Alipay (¥19/mo)',
  'upgrade.pay.qrPlaceholder1': 'Payment QR code',
  'upgrade.pay.qrPlaceholder2': '(shown once connected)',
  'upgrade.pay.alipayHint': 'Scan with Alipay to pay ¥19/mo',
  'upgrade.pay.autoNote': 'Upgrades automatically after payment — nothing else to do',
  'upgrade.pay.wxpay': 'WeChat Pay (¥19/30 days)',
  'upgrade.pay.waffo': 'Card payment ($3/mo)',
  'upgrade.pay.wxpayHint': 'Scan with WeChat to pay ¥19 for 30 days of Pro',
  'upgrade.pay.xunhupayAlipayHint': 'Scan with Alipay to pay ¥19 for 30 days of Pro',
  'upgrade.pay.xunhupayNote': 'One-time 30-day purchase; buy again anytime to extend, auto downgrade on expiry',
  'upgrade.pay.waffoNote': 'Visa / Mastercard / Apple Pay / Google Pay, auto-renewing',
  'upgrade.pay.later': 'Maybe later',
  'upgrade.notes.title': 'Notes',
  'upgrade.notes.1': '• Monthly billing; downgrades to Free at expiry (no auto-renewal)',
  'upgrade.notes.2': '• Both payment channels give identical benefits',
  'upgrade.notes.3': '• Your APK is rebuilt automatically after upgrading (watermark removed)',
  'upgrade.notes.4': '• Payment issues: {email}',

  // ---------- Subscription details (settings) ----------
  'billing.paymentMethod': 'Payment method',
  'billing.amount': 'Amount',
  'billing.started': 'Started',
  'billing.renewsOn': 'Renews on',
  'billing.canceledAt': 'Canceled at',
  'billing.cancelAtEnd': '(cancels at end of period)',
  'billing.cancelSub': 'Cancel subscription',
  'billing.cancelHint': 'Your subscription stays active until the end of the current billing period. No further charges will be made.',
  'billing.keepSub': 'Keep subscription',
  'billing.confirmCancel': 'Confirm cancel',
  'billing.canceling': 'Canceling…',
  'billing.noPaymentMethods': 'No payment methods configured',
  'billing.waitingPayment': 'Waiting for payment…',
  'billing.paymentSuccess': 'Payment successful! Your plan has been upgraded.',

  // ---------- Footer ----------
  'footer.privacy': 'Privacy Policy',
  'footer.terms': 'Terms of Service',

  // ---------- Legal pages: Privacy Policy ----------
  'legal.privacy.title': 'Privacy Policy',
  'legal.privacy.updated': 'Last updated: September 2026',
  'legal.privacy.intro':
    'Cloupak ("we") respects and protects your privacy. This policy explains how we collect, use, store, and share your personal information when you use the Cloupak service, and the rights you have under applicable law. By using the service, you confirm that you have read and agree to this policy.',

  'legal.privacy.s1.title': '1. Data Controller',
  'legal.privacy.s1.p1':
    'This service is operated by the Cloupak operator (an individual / sole trader operating under the laws of China, "we"), who is the data controller of this service.',
  'legal.privacy.s1.p2':
    'Privacy contact email: {email}; Data Protection Officer (DPO): not appointed.',

  'legal.privacy.s2.title': '2. Information We Collect',
  'legal.privacy.s2.p1':
    'Information you provide — Account information: the email address, username, and password you provide at sign-up (passwords are stored only as hashes — we cannot read them). App data: the project source code and build configuration you upload, and the key-value data your apps store through the data sync SDK.',
  'legal.privacy.s2.p2':
    'Information collected automatically — Device and network: IP address, browser type, and time zone. Usage behavior: build history, sign-in times, and feature usage records. Log data: request times, error logs, and other operational logs used to keep the service stable and secure.',
  'legal.privacy.s2.p3':
    'Payment information: transaction amounts and payment status only. We never store full card numbers — card data is processed directly by the payment processor (Waffo Pancake, etc.) and never passes through our servers.',

  'legal.privacy.s3.title': '3. How We Use Your Information',
  'legal.privacy.s3.p1':
    'To provide and maintain the service, process billing and payments, and provide customer support (legal basis: performance of contract); to send service notices (billing, security, policy updates) and for security and fraud prevention (legal basis: legitimate interest).',
  'legal.privacy.s3.p2':
    'Product improvement and analysis: we may aggregate or de-identify data for statistical analysis; such data cannot identify any individual (legal basis: legitimate interest).',
  'legal.privacy.s3.p3':
    'Legal compliance: processing data as required by applicable law (legal basis: legal obligation). We currently do not send marketing communications.',

  'legal.privacy.s4.title': '4. Local Storage & Tracking Technologies',
  'legal.privacy.s4.p1':
    'We only store strictly necessary data (your sign-in token) and functional data (language preference) in the browser localStorage. We do not use analytics or marketing cookies, nor any third-party analytics or tracking scripts.',

  'legal.privacy.s5.title': '5. Sharing and Disclosure of Personal Information',
  'legal.privacy.s5.p1':
    'We do not sell your personal information, including as "sale" is defined under applicable law (such as the CCPA).',
  'legal.privacy.s5.p2':
    'Service providers: cloud computing and builds (Cloudflare, GitHub Actions), bound by data processing agreements; payment card data is processed exclusively by our PCI-DSS-compliant payment processor Waffo Pancake and is never stored on our servers; other payment channels (Alipay, Xunhupay) likewise process payments directly — we only receive the transaction result.',
  'legal.privacy.s5.p3':
    'Legal and regulatory requirements: disclosure pursuant to law, court order, or valid requests from authorities; business transactions: in the event of a merger or acquisition we will notify you in advance and ensure protection obligations continue; any other use with your prior consent.',

  'legal.privacy.s6.title': '6. Data Security Measures',
  'legal.privacy.s6.p1':
    'All network traffic is encrypted with TLS/HTTPS; passwords are stored as hashes and sensitive credentials such as payment keys are stored encrypted; data is strictly isolated per app and per user, following the principle of least privilege. Data is stored on Cloudflare\u2019s global infrastructure (Workers, D1, R2, KV).',
  'legal.privacy.s6.p2':
    'If a security incident affects your rights, we will notify you and the relevant regulators within 72 hours of discovery, as required by law. Please keep your account credentials safe and do not share them.',

  'legal.privacy.s7.title': '7. Data Retention Periods',
  'legal.privacy.s7.p1':
    'Account information: retained while your account is active; deleted or anonymized within 90 days after account closure. App data: retained while the app exists; deleting an app also deletes its source code and associated data.',
  'legal.privacy.s7.p2':
    'Transaction records: retained for 5 years to meet accounting and tax requirements. Support records: retained for 2 years. Security and operational logs: retained for 12 months. Data is securely deleted upon expiry.',

  'legal.privacy.s8.title': '8. Your Data Rights',
  'legal.privacy.s8.p1':
    'You have the rights to be informed, to access, to rectify, and to erase your personal data, as well as to restrict processing, to obtain a machine-readable copy (data portability), and to object to processing based on legitimate interest. To exercise these rights, contact us via the email below and we will respond within 30 calendar days.',
  'legal.privacy.s8.p2':
    'If you believe we have not handled your request properly, you have the right to complain to your local data protection authority.',

  'legal.privacy.s9.title': '9. Cross-Border Data Transfers',
  'legal.privacy.s9.p1':
    'Our servers and service providers (Cloudflare, etc.) may be located outside your country. For cross-border transfers, we safeguard your data through data processing agreements incorporating EU Standard Contractual Clauses (SCCs) and by transferring only to recipients with an equivalent level of protection.',

  'legal.privacy.s10.title': '10. Minors',
  'legal.privacy.s10.p1':
    'This service is intended for users aged 18 and above. We do not knowingly collect information from anyone below that age. If you believe a minor has provided us information, please contact us immediately and we will delete it as soon as possible.',

  'legal.privacy.s11.title': '11. Policy Changes',
  'legal.privacy.s11.p1':
    'In the event of significant changes, we will give at least 15 days\u2019 notice via in-app announcement or your registered email and update the "Last updated" date above. Continued use after the effective date constitutes acceptance.',

  'legal.privacy.s12.title': '12. Contact Us',
  'legal.privacy.s12.p1': 'For any privacy-related questions or data rights requests, contact: {email}',

  // ---------- Legal pages: Terms of Service ----------
  'legal.terms.title': 'Terms of Service',
  'legal.terms.updated': 'Last updated: September 2026',
  'legal.terms.intro':
    'Welcome to Cloupak. The service is provided by the Cloupak operator (an individual / sole trader operating under the laws of China). By using the service you agree to the terms below. Please read them carefully.',

  'legal.terms.s1.title': '1. Acceptance of Terms',
  'legal.terms.s1.p1':
    'By creating an account, subscribing to any plan, or otherwise using the service, you confirm that you: (a) are at least 18 years of age; (b) have read, understood, and agree to be bound by these Terms; (c) agree to our Privacy Policy; and (d) are authorized to enter into this agreement on behalf of yourself or any organization you represent.',
  'legal.terms.s1.p2':
    'Important: If you do not agree to these Terms, you may not use the service. Continued use after any update constitutes acceptance of the revised Terms.',

  'legal.terms.s2.title': '2. Description of the Service',
  'legal.terms.s2.p1':
    'Cloupak provides automated packaging of web projects into Android apps with cloud hosting, including auto-deployment, data sync, APK build, and distribution. It is a digital Software-as-a-Service (SaaS) product delivered over the internet.',
  'legal.terms.s2.p2':
    'The service is a digital, intangible product. Access is granted immediately after payment confirmation. Due to the instant-delivery nature of digital services, refunds are subject to specific limitations set out in Section 7.',

  'legal.terms.s3.title': '3. Accounts',
  'legal.terms.s3.p1':
    'You need an account to use the service and must provide accurate and complete registration information. You are responsible for safeguarding your credentials; all activity under your account is treated as your own. Do not share your credentials with any third party.',
  'legal.terms.s3.p2':
    'Please keep your billing email address valid so you can receive receipts and subscription notices. If you register on behalf of a company or organization, you confirm you are authorized to enter into this agreement for that entity.',

  'legal.terms.s4.title': '4. Plans & Pricing',
  'legal.terms.s4.p1':
    'The Free plan provides a basic quota; the Pro plan is billed monthly (¥19/mo or $3/mo depending on the payment channel). See the pricing page on our website for current plan details. We may adjust plan features at any time; price changes follow Section 5.',

  'legal.terms.s5.title': '5. Billing & Payments',
  'legal.terms.s5.p1':
    'Auto-renewal authorization: by subscribing to a paid plan via card payment, you expressly authorize Cloupak to charge your payment method the subscription fee for each billing period, until you cancel under Section 6. Your subscription renews automatically at the end of each billing period unless you cancel.',
  'legal.terms.s5.p2':
    'Domestic channels (Alipay/WeChat) are one-time 30-day purchases with no automatic charges; they can be stacked to extend your plan.',
  'legal.terms.s5.p3':
    'Taxes: unless otherwise stated, listed prices exclude applicable taxes; where required by law, we will collect and remit them.',
  'legal.terms.s5.p4':
    'Payment processing: card payments are handled by our PCI-DSS-compliant payment processor Waffo Pancake — card data is never stored on our servers; Alipay/WeChat payments are processed directly by those providers and we only receive the transaction result.',

  'legal.terms.s6.title': '6. Cancellation Policy',
  'legal.terms.s6.p1':
    'You may cancel at any time via either of the following: online (recommended) — sign in and go to "Settings → Subscription → Cancel subscription"; or send a cancellation request from your registered email to {email}. Cancellation is registered immediately; the service remains active until the end of the current billing period, and you will not be charged again afterward.',
  'legal.terms.s6.p2':
    'After cancellation, account data is retained for 90 days and then deleted in accordance with the Privacy Policy. You may contact us at any time to close your account.',

  'legal.terms.s7.title': '7. Refund Policy',
  'legal.terms.s7.p1':
    'Because the service is a digital, instantly delivered product, subscription fees are generally non-refundable, with the following exceptions.',
  'legal.terms.s7.p2':
    'Refundable situations: (a) duplicate charges — full refund for duplicate charges caused by billing errors; (b) service outage — outages caused by us lasting more than 72 hours qualify for a pro-rated refund or extension; (c) statutory rights — where applicable law grants withdrawal or refund rights (such as the EU/UK 14-day cooling-off period), those rights are preserved.',
  'legal.terms.s7.p3':
    'New-subscriber 7-day guarantee: first-time subscribers may request a full refund within 7 days of the first charge (provided usage has not exceeded 20% of the plan quota).',
  'legal.terms.s7.p4':
    'How to request: email {email} with your account email, transaction ID, and reason. We will confirm within 2 business days and refund to the original payment method within 5–10 business days.',

  'legal.terms.s8.title': '8. Billing Disputes',
  'legal.terms.s8.p1':
    'If you believe a charge is incorrect, please email {email} first and contact us before initiating a dispute with your bank. We commit to responding within 2 business days and resolving confirmed billing errors within 5 business days.',

  'legal.terms.s9.title': '9. Your Content & Intellectual Property',
  'legal.terms.s9.p1':
    'The code and app data you upload remain yours. To provide build and hosting services, you grant us the temporary processing license necessary to do so — beyond that we will not use your content for any other purpose. You are responsible for ensuring your uploads are lawful and do not infringe any third-party rights.',
  'legal.terms.s9.p2':
    'The Cloupak platform (website, build system, data sync SDK, etc.) is our intellectual property and may not be copied, resold, or used commercially without permission.',

  'legal.terms.s10.title': '10. Acceptable Use',
  'legal.terms.s10.p1':
    'You may not use the service to: create or distribute malware, phishing content, or network attack tools; distribute infringing, illegal, defamatory, or fraudulent content; create deepfake content impersonating real individuals; scrape or abuse the service; bypass or interfere with its security features; resell access without prior approval; or violate any applicable law or card network rules.',
  'legal.terms.s10.p2':
    'We may suspend or terminate service for accounts that violate these rules.',

  'legal.terms.s11.title': '11. Data, Privacy & Security',
  'legal.terms.s11.p1':
    'Your use of the service is governed by our Privacy Policy, published at cloupak.app/privacy. Payment card data is processed exclusively by our PCI-DSS-compliant payment processor Waffo Pancake and is never stored on our servers. Account data is retained for 90 days after cancellation and then deleted.',

  'legal.terms.s12.title': '12. Disclaimer & Limitation of Liability',
  'legal.terms.s12.p1':
    'The service is provided "as is" and "as available," without any express or implied warranties, including merchantability or fitness for a particular purpose. We do not guarantee uninterrupted or error-free operation; you should keep your own backups of source code and other important data.',
  'legal.terms.s12.p2':
    'To the maximum extent permitted by law, we are not liable for any indirect, incidental, or consequential damages; our aggregate liability is capped at the amount you actually paid us in the 12 months before the event giving rise to the claim.',

  'legal.terms.s13.title': '13. Term & Termination',
  'legal.terms.s13.p1':
    'These Terms remain in effect while you use the service. We may suspend or terminate your account if you materially breach these Terms, engage in fraud, or as required by law. If we terminate for reasons other than your breach, we will refund the unused, prepaid portion pro rata. You may delete your account at any time by contacting us.',

  'legal.terms.s14.title': '14. Changes to These Terms',
  'legal.terms.s14.p1':
    'We may update these Terms at any time; significant changes will be notified at least 14 days before taking effect via email or in-app announcement. Continued use constitutes acceptance. If any provision is held invalid, the remainder stays in full force. These Terms and the Privacy Policy together form the entire agreement between you and us.',

  'legal.terms.s15.title': '15. Governing Law & Dispute Resolution',
  'legal.terms.s15.p1':
    'These Terms are governed by the laws of the People\u2019s Republic of China. Before initiating formal proceedings, please email {email} first and attempt an informal resolution.',

  'legal.terms.s16.title': '16. Contact',
  'legal.terms.s16.p1':
    'For support, billing, refunds, cancellations, and legal matters, contact: {email}. By using the service or agreeing to these Terms at checkout, you confirm that you have read and agreed to them.',
};
